import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { UserContext } from "../../../context/UserContext";
import { useInVenueUsers } from "../../../context/useInVenueUsers";
import apiFetch, { safeJson } from "../../../context/api";
import MemberPhoto from "../../Elements/MemberPhoto";
import { getImageUrl } from "../../../lib/helpers";
import notFoundImage from "../../../assets/fox-not-found.webp";
import AddCashModal from "../../Elements/AddCashModal";
import QRCode from "react-qr-code";
import EntryTimer from "../ControlComponents/EntryTimer";

/**
 * Entradas del CLUB SOCIAL (CRUO F1) — página propia, separada del Control del
 * POS de cannabis (docs/ARQUITECTURA_CARRITO_V2.md §estrangulamiento).
 *
 * Principios que arreglan el "socio atascado" del flujo anterior:
 *  - UNA sola fuente de verdad: `atendiendo` (el socio seleccionado). Sin URL-sync,
 *    sin localStorage, sin estado espejo en un embed.
 *  - Carrito = Cart v2 NATIVO (/v1/cart, context pos_productos) con CAS: el carrito
 *    vive en el servidor por socio; cambiar de socio nunca pierde nada.
 *  - "Enviar a Barra" convierte el carrito en comanda (checkout-to-order).
 */

const CONTEXT = "pos_productos";

function eur(n) {
  return `${Number(n || 0).toFixed(2)} €`;
}

export default function ClubEntradas() {
  const [token, , , venueID] = useContext(UserContext);
  const {
    usersInVenue,
    markUserEntered,
    markUserLeft,
    refresh: refreshSala,
    updateUserCredit,
  } = useInVenueUsers();

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token]
  );

  // ── sala ──────────────────────────────────────────────────────────────────
  const [salaSearch, setSalaSearch] = useState("");
  const [atendiendo, setAtendiendo] = useState(null); // socio seleccionado (objeto de sala)
  const [msg, setMsg] = useState(null); // {tone, text}

  const salaFiltrada = useMemo(() => {
    const q = salaSearch.trim().toLowerCase();
    if (!q) return usersInVenue;
    return usersInVenue.filter((u) => {
      const nombre = `${u?.profile?.first_name || ""} ${u?.profile?.last_name || ""}`.toLowerCase();
      const num = String(u?.profile?.membership_id ?? u?.profile?.legacy_member_id ?? "");
      return nombre.includes(q) || num.includes(q) || (u?.email || "").toLowerCase().includes(q);
    });
  }, [usersInVenue, salaSearch]);

  // Si el socio atendido sale del club, se suelta solo (sin bucles: estado único).
  useEffect(() => {
    if (atendiendo && !usersInVenue.some((u) => String(u.id) === String(atendiendo.id))) {
      setAtendiendo(null);
    }
  }, [usersInVenue, atendiendo]);

  // ── productos ─────────────────────────────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [prodSearch, setProdSearch] = useState("");
  const [categoria, setCategoria] = useState("Todo");

  useEffect(() => {
    if (!token || !venueID) return;
    let cancelled = false;
    (async () => {
      try {
        const PAGE = 200;
        let skip = 0;
        let all = [];
        for (;;) {
          const res = await apiFetch(
            `/v1/get-venue-products?exclude_dispensario=true&skip=${skip}&limit=${PAGE}`,
            { headers: { ...authHeaders, "X-Venue-ID": venueID } }
          );
          if (!res.ok) throw new Error("No se pudieron cargar los productos");
          const batch = await safeJson(res);
          all = all.concat(Array.isArray(batch) ? batch : []);
          if (!Array.isArray(batch) || batch.length < PAGE) break;
          skip += PAGE;
        }
        if (!cancelled) setProducts(all.filter((p) => !p.is_dispensario));
      } catch (e) {
        if (!cancelled) setMsg({ tone: "err", text: e.message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, venueID, authHeaders]);

  const categorias = useMemo(() => {
    const set = new Set();
    for (const p of products) if (p?.category?.name) set.add(p.category.name);
    return ["Todo", ...Array.from(set).sort()];
  }, [products]);

  const productosVisibles = useMemo(() => {
    const q = prodSearch.trim().toLowerCase();
    return products.filter(
      (p) =>
        (categoria === "Todo" || p?.category?.name === categoria) &&
        (!q || (p.name || "").toLowerCase().includes(q))
    );
  }, [products, categoria, prodSearch]);

  // ── mesas (service_tables): para "servir en mesa" desde el staff ──────────
  const [mesas, setMesas] = useState([]);
  const [mesaId, setMesaId] = useState(""); // "" = recoge en barra

  useEffect(() => {
    if (!token || !venueID) return;
    (async () => {
      try {
        const res = await apiFetch(`/v1/service-tables?venue_id=${venueID}`, { headers: authHeaders });
        if (res.ok) setMesas((await safeJson(res)) || []);
      } catch {
        /* sin mesas no pasa nada: todo será recogida en barra */
      }
    })();
  }, [token, venueID, authHeaders]);

  // ── carrito (Cart v2, servidor = fuente de verdad) ────────────────────────
  const [cart, setCart] = useState({ items: [], version: 0 });
  const [cartBusy, setCartBusy] = useState(false);
  const versionRef = useRef(0);
  versionRef.current = cart.version;

  const loadCart = useCallback(
    async (socioId) => {
      if (!socioId) return;
      const res = await apiFetch(
        `/v1/cart?venue_id=${venueID}&owner_user_id=${socioId}&context=${CONTEXT}`,
        { headers: authHeaders }
      );
      if (!res.ok) return;
      const data = await safeJson(res);
      setCart({ items: data?.items || [], version: data?.version || 0 });
    },
    [venueID, authHeaders]
  );

  useEffect(() => {
    setCart({ items: [], version: 0 });
    if (atendiendo?.id) loadCart(atendiendo.id);
  }, [atendiendo?.id, loadCart]);

  const saveCart = useCallback(
    async (nextItems, { retry = true } = {}) => {
      if (!atendiendo?.id) return;
      setCartBusy(true);
      try {
        const res = await apiFetch(`/v1/cart`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({
            venue_id: venueID,
            owner_user_id: atendiendo.id,
            context: CONTEXT,
            base_version: versionRef.current,
            table_id: mesaId || null,
            items: nextItems.map((it) => ({
              product_id: it.product_id,
              quantity: it.quantity,
              unit_price: it.unit_price,
              location_id: it.location_id || null,
            })),
          }),
        });
        if (res.status === 409 && retry) {
          // CAS: otra pantalla ganó — recargar y reintentar UNA vez sobre lo fresco.
          await loadCart(atendiendo.id);
          return saveCart(nextItems, { retry: false });
        }
        if (!res.ok) {
          const detail = (await res.json().catch(() => null))?.detail;
          throw new Error(typeof detail === "string" ? detail : "No se pudo guardar el carrito");
        }
        const data = await safeJson(res);
        setCart({ items: data?.items || [], version: data?.version || 0 });
        setMsg(null);
      } catch (e) {
        setMsg({ tone: "err", text: e.message });
      } finally {
        setCartBusy(false);
      }
    },
    [atendiendo?.id, venueID, authHeaders, loadCart, mesaId]
  );

  const addProduct = useCallback(
    (p) => {
      const items = [...cart.items];
      const idx = items.findIndex((it) => it.product_id === p.id && !it.location_id);
      if (idx >= 0) items[idx] = { ...items[idx], quantity: Number(items[idx].quantity) + 1 };
      else items.push({ product_id: p.id, quantity: 1, unit_price: Number(p.price || 0), _name: p.name });
      saveCart(items);
    },
    [cart.items, saveCart]
  );

  const setQty = useCallback(
    (productId, qty) => {
      const items = cart.items
        .map((it) => (it.product_id === productId ? { ...it, quantity: qty } : it))
        .filter((it) => Number(it.quantity) > 0);
      saveCart(items);
    },
    [cart.items, saveCart]
  );

  const nameOf = useCallback(
    (productId) => products.find((p) => p.id === productId)?.name || "Producto",
    [products]
  );

  const total = cart.items.reduce((s, it) => s + Number(it.quantity) * Number(it.unit_price || 0), 0);
  const saldo = Number(atendiendo?.credit_balance ?? atendiendo?.profile?.credit_balance ?? 0);

  // ── enviar a barra ────────────────────────────────────────────────────────
  const enviarABarra = useCallback(async () => {
    if (!atendiendo?.id || cart.items.length === 0 || cartBusy) return;
    setCartBusy(true);
    try {
      // Fijar la mesa en el carrito del servidor justo antes de convertir
      // (el conversor deriva serve/pickup de cart.table_id).
      await apiFetch(`/v1/cart`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          venue_id: venueID,
          owner_user_id: atendiendo.id,
          context: CONTEXT,
          base_version: versionRef.current,
          table_id: mesaId || null,
          items: cart.items.map((it) => ({
            product_id: it.product_id,
            quantity: it.quantity,
            unit_price: it.unit_price,
            location_id: it.location_id || null,
          })),
        }),
      }).then(async (r) => {
        if (r.ok) {
          const d = await safeJson(r);
          setCart({ items: d?.items || [], version: d?.version || 0 });
          versionRef.current = d?.version || 0;
        }
      });
      const res = await apiFetch(`/v1/cart/checkout-to-order`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          venue_id: venueID,
          owner_user_id: atendiendo.id,
          context: CONTEXT,
          base_version: versionRef.current,
        }),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => null))?.detail;
        throw new Error(typeof detail === "string" ? detail : "No se pudo enviar la comanda");
      }
      const order = await safeJson(res);
      setCart({ items: [], version: 0 });
      setMesaId("");
      setMsg({
        tone: "ok",
        text: `Comanda de ${atendiendo?.profile?.first_name || "socio"} enviada a Barra ✓ (${
          order?.fulfillment === "serve" ? "servir en mesa" : "recoge en barra"
        })`,
      });
    } catch (e) {
      setMsg({ tone: "err", text: e.message });
    } finally {
      setCartBusy(false);
    }
  }, [atendiendo, cart.items, cartBusy, venueID, authHeaders, mesaId]);

  // ── entrada / salida ──────────────────────────────────────────────────────
  const [saldoUserId, setSaldoUserId] = useState(null); // socio del modal "+ Saldo"
  const [vinculo, setVinculo] = useState(null); // {socio, url} → modal QR de vinculación
  const [showNueva, setShowNueva] = useState(false);
  const [nuevaQ, setNuevaQ] = useState("");
  const [nuevaResults, setNuevaResults] = useState([]);
  const [nuevaBuscando, setNuevaBuscando] = useState(false);

  useEffect(() => {
    if (!showNueva || nuevaQ.trim().length < 2) {
      setNuevaResults([]);
      return undefined;
    }
    setNuevaBuscando(true);
    const t = setTimeout(async () => {
      try {
        const res = await apiFetch(
          `/v1/clients/search/${venueID}?q=${encodeURIComponent(nuevaQ.trim())}&limit=20`,
          { headers: authHeaders }
        );
        if (res.ok) setNuevaResults((await safeJson(res)) || []);
      } catch {
        /* búsqueda silenciosa */
      } finally {
        setNuevaBuscando(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [showNueva, nuevaQ, venueID, authHeaders]);

  const registrarEntrada = useCallback(
    async (client) => {
      try {
        const res = await apiFetch(`/v1/entries`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({ user_id: client.id, venue_id: venueID }),
        });
        if (!res.ok) throw new Error("No se pudo registrar la entrada");
        markUserEntered({ ...client, entry_time: new Date() });
        setShowNueva(false);
        setNuevaQ("");
        refreshSala?.();
      } catch (e) {
        setMsg({ tone: "err", text: e.message });
      }
    },
    [authHeaders, venueID, markUserEntered, refreshSala]
  );

  const vincularMovil = useCallback(
    async (socio) => {
      try {
        const res = await apiFetch(`/v1/member/link-session`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({ venue_id: venueID, member_user_id: socio.id }),
        });
        if (!res.ok) throw new Error("No se pudo generar el QR de vinculación");
        const data = await safeJson(res);
        // El QR usa SIEMPRE el origen desde el que el staff abrió la Sala: si la
        // pantalla está en http://192.168.x.x:3001, el móvil del socio (misma wifi)
        // podrá llegar. El FRONTEND_URL del backend (localhost en dev) no sirve aquí.
        const url = `${window.location.origin}/app/vincular?token=${encodeURIComponent(data.link_token)}`;
        setVinculo({ socio, url, min: data.expires_in_minutes });
      } catch (e) {
        setMsg({ tone: "err", text: e.message });
      }
    },
    [authHeaders, venueID]
  );

  const registrarSalida = useCallback(
    async (socio) => {
      try {
        const res = await apiFetch(`/v1/checkout?user_id=${socio.id}`, {
          method: "PATCH",
          headers: authHeaders,
        });
        if (!res.ok) throw new Error("No se pudo registrar la salida");
        markUserLeft(socio.id);
        if (String(atendiendo?.id) === String(socio.id)) setAtendiendo(null);
      } catch (e) {
        setMsg({ tone: "err", text: e.message });
      }
    },
    [authHeaders, markUserLeft, atendiendo?.id]
  );

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex max-w-full flex-col gap-4 overflow-x-hidden p-4">
      {/* ── Caja 1: la SALA (jerarquía del diseño cannabis: cajas blancas sobre fondo gris) ── */}
      <section className="rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-sm sm:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">
          Sala{" "}
          <span className="ml-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-sm font-semibold text-gray-600">
            {usersInVenue.length} dentro
          </span>
        </h1>
        <div className="flex items-center gap-2">
          <input
            value={salaSearch}
            onChange={(e) => setSalaSearch(e.target.value)}
            placeholder="Buscar socio…"
            className="h-10 w-56 rounded-lg border border-gray-300 px-3 text-sm"
          />
          <button
            type="button"
            onClick={() => setShowNueva(true)}
            className="h-10 rounded-lg bg-[#0A0A0A] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#262626]"
          >
            Nueva entrada
          </button>
        </div>
      </div>

      {msg && (
        <div
          role="status"
          className={`mb-3 rounded-lg border px-4 py-2 text-sm ${
            msg.tone === "ok"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-rose-200 bg-rose-50 text-rose-700"
          }`}
        >
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {salaFiltrada.length === 0 && (
          <div className="col-span-full rounded-xl border border-dashed border-gray-300 p-8 text-center text-gray-400">
            No hay socios en sala.
          </div>
        )}
        {salaFiltrada.map((u) => {
          const activo = String(atendiendo?.id) === String(u.id);
          const cred = Number(u?.credit_balance ?? u?.profile?.credit_balance ?? 0);
          return (
            <div
              key={u.id}
              className={`rounded-xl border bg-white p-3 shadow-sm transition-shadow ${
                activo ? "border-[#16A34A] ring-1 ring-[#16A34A]" : "border-[#E5E5E5] hover:shadow-md"
              }`}
            >
              {/* Fila 1: foto + nombre + saldo/tiempo. Fila 2: botones (con wrap).
                  Apilado a propósito: no puede desbordarse a ningún ancho. */}
              <div className="flex items-center gap-3">
                <MemberPhoto
                  path={u?.profile?.image_path || u?.profile?.documents_path}
                  name={u?.profile?.first_name || u?.email || ""}
                  className="h-11 w-11 flex-shrink-0 rounded-lg object-cover"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-[#0A0A0A]">
                      {`${u?.profile?.first_name || ""} ${u?.profile?.last_name || ""}`.trim() ||
                        u?.email ||
                        "Socio"}
                    </span>
                    <span className="inline-flex flex-shrink-0 rounded-full bg-[#DCFCE7] px-2 py-0.5 text-[10px] font-semibold text-[#16A34A]">
                      En sala
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                    {(u?.profile?.membership_id ?? u?.profile?.legacy_member_id) != null && (
                      <span className="rounded bg-[#F5F5F5] px-2 py-0.5 font-medium text-[#525252]">
                        Nº Socio: {u?.profile?.membership_id ?? u?.profile?.legacy_member_id}
                      </span>
                    )}
                    <span className={`rounded px-2 py-0.5 font-medium ${cred < 0 ? "bg-red-50 text-[#DC2626]" : "bg-[#F5F5F5] text-[#525252]"}`}>
                      Crédito: <b className={cred < 0 ? "text-[#DC2626]" : "text-[#0A0A0A]"}>{eur(cred)}</b>
                    </span>
                    <span className="text-[#737373]">
                      {u?.entry_time ? <EntryTimer entryTime={u.entry_time} /> : "—"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {activo ? (
                  <button
                    type="button"
                    onClick={() => setAtendiendo(null)}
                    className="rounded-md border border-[#E5E5E5] bg-[#F7F7F3] px-3.5 py-1.5 text-xs font-semibold text-[#0A0A0A]"
                  >
                    Cerrar
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setAtendiendo(u)}
                    className="rounded-md bg-[#0A0A0A] px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#262626]"
                  >
                    Atender
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSaldoUserId(u.id)}
                  className="rounded-md bg-lime-500 px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-lime-600"
                >
                  + Saldo
                </button>
                <button
                  type="button"
                  onClick={() => vincularMovil(u)}
                  className="rounded-md border border-[#E5E5E5] px-3 py-1.5 text-xs font-medium text-[#525252] hover:bg-[#F7F7F3]"
                  title="Vincular el móvil del socio (QR de acceso a la app)"
                >
                  📱
                </button>
                <button
                  type="button"
                  onClick={() => registrarSalida(u)}
                  className="ml-auto rounded-md border border-[#E5E5E5] px-3.5 py-1.5 text-xs font-medium text-[#737373] hover:bg-[#F7F7F3]"
                >
                  Salida
                </button>
              </div>
            </div>
          );
        })}
      </div>
      </section>

      {/* ── Hero "¿A quién atendemos?" cuando no hay socio (igual que cannabis) ── */}
      {!atendiendo && (
        <section className="rounded-2xl border border-[#E5E5E5] bg-white p-8 shadow-sm">
          <h2 className="text-2xl font-semibold tracking-tight text-[#0A0A0A]">¿A quién atendemos?</h2>
          <p className="mt-1 text-[#737373]">Busca un socio en sala para añadir productos.</p>
        </section>
      )}

      {/* Atención: productos + carrito */}
      {atendiendo && (
        <>
        {/* ── Banner del socio (diseño cannabis: borde verde, chips, Cambiar socio) ── */}
        <section className="rounded-xl border-2 border-[#16A34A] bg-white p-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            <MemberPhoto
              path={atendiendo?.profile?.image_path || atendiendo?.profile?.documents_path}
              name={atendiendo?.profile?.first_name || atendiendo?.email || ""}
              className="h-[46px] w-[46px] flex-shrink-0 rounded-lg object-cover"
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate text-base font-semibold text-[#0A0A0A]">
                  {`${atendiendo?.profile?.first_name || ""} ${atendiendo?.profile?.last_name || ""}`.trim() || atendiendo?.email || "Socio"}
                </span>
                <span className="inline-flex flex-shrink-0 rounded-full bg-[#DCFCE7] px-2 py-0.5 text-[10px] font-semibold text-[#16A34A]">
                  En sala
                </span>
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5 text-[11px]">
                {(atendiendo?.profile?.membership_id ?? atendiendo?.profile?.legacy_member_id) != null && (
                  <span className="rounded bg-[#F5F5F5] px-2 py-0.5 font-medium text-[#525252]">
                    Nº Socio: {atendiendo?.profile?.membership_id ?? atendiendo?.profile?.legacy_member_id}
                  </span>
                )}
                <span className={`rounded px-2 py-0.5 font-medium ${saldo < 0 ? "bg-red-50 text-[#DC2626]" : "bg-[#F5F5F5] text-[#525252]"}`}>
                  Crédito: <b className={saldo < 0 ? "text-[#DC2626]" : "text-[#0A0A0A]"}>{eur(saldo)}</b>
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setAtendiendo(null)}
              className="ml-auto inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md border border-[#E5E5E5] bg-white px-3 text-sm font-medium text-[#0A0A0A] transition-colors hover:bg-[#F7F7F3]"
            >
              ⟳ Cambiar socio
            </button>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-sm lg:col-span-2">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {categorias.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategoria(c)}
                  className={`rounded-full px-3 py-1 text-sm transition-colors ${
                    categoria === c ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] text-[#525252] hover:bg-[#E5E5E5]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative mb-4">
              <input
                value={prodSearch}
                onChange={(e) => setProdSearch(e.target.value)}
                placeholder="Buscar producto..."
                className="h-11 w-full rounded-lg border border-[#E5E5E5] bg-white px-4 text-sm text-[#0A0A0A] placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
              {productosVisibles.map((p) => (
                <div
                  key={p.id}
                  className="flex flex-col rounded-xl border border-[#E5E5E5] bg-white p-1.5 text-xs shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="relative w-full overflow-hidden rounded-lg bg-[#F8FAFC]" style={{ paddingTop: "100%" }}>
                    <img
                      src={(p.image_path ? getImageUrl(p.image_path) : null) || notFoundImage}
                      alt={p.name}
                      loading="lazy"
                      decoding="async"
                      className="absolute left-0 top-0 h-full w-full object-cover"
                      onError={(e) => {
                        e.target.src = notFoundImage;
                      }}
                    />
                  </div>
                  <span className="mt-1 inline-flex w-fit rounded bg-[#F5F5F5] px-2 py-0.5 text-[10px] font-medium text-[#525252]">
                    {p?.category?.name || "Sin categoría"}
                  </span>
                  <h3 className="mt-0.5 truncate text-sm font-medium leading-tight text-[#0A0A0A]">{p.name}</h3>
                  <p className="mb-1 text-[11px] text-[#737373]">{Number(p.price || 0).toFixed(2)} €/u</p>
                  <button
                    type="button"
                    disabled={cartBusy}
                    onClick={() => addProduct(p)}
                    className="mt-auto inline-flex w-full touch-manipulation items-center justify-center gap-1 rounded-md bg-lime-500 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-lime-600 active:scale-95 disabled:bg-[#F5F5F5] disabled:text-[#A3A3A3]"
                  >
                    + Añadir
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-sm lg:sticky lg:top-4 lg:self-start">
            <h3 className="text-lg font-semibold text-[#0A0A0A]">Carrito</h3>
            <div className="mb-3 mt-0.5 truncate text-xs text-[#737373]">
              {`${atendiendo?.profile?.first_name || ""} ${atendiendo?.profile?.last_name || ""}`.trim() || "Socio"}
              {(atendiendo?.profile?.membership_id ?? atendiendo?.profile?.legacy_member_id) != null &&
                ` · Nº Socio: ${atendiendo?.profile?.membership_id ?? atendiendo?.profile?.legacy_member_id}`}
            </div>

            {cart.items.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[#E5E5E5] p-6 text-center text-sm text-[#A3A3A3]">
                Toca productos para añadirlos.
              </div>
            ) : (
              <>
              <div className="mb-1 flex items-center justify-between border-b border-[#F5F5F5] pb-1 text-[10px] font-semibold uppercase tracking-wide text-[#A3A3A3]">
                <span>Producto</span>
                <span className="pr-1">Cantidad · Precio</span>
              </div>
              <ul className="space-y-2">
                {cart.items.map((it) => (
                  <li key={`${it.product_id}-${it.location_id || ""}`} className="flex items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 flex-1 truncate">{it._name || nameOf(it.product_id)}</span>
                    <span className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={cartBusy}
                        onClick={() => setQty(it.product_id, Number(it.quantity) - 1)}
                        className="h-6 w-6 rounded border border-[#E5E5E5] leading-none text-[#525252] hover:bg-[#F7F7F3]"
                      >
                        −
                      </button>
                      <b className="w-6 text-center tabular-nums">{Number(it.quantity)}</b>
                      <button
                        type="button"
                        disabled={cartBusy}
                        onClick={() => setQty(it.product_id, Number(it.quantity) + 1)}
                        className="h-6 w-6 rounded border border-[#E5E5E5] leading-none text-[#525252] hover:bg-[#F7F7F3]"
                      >
                        +
                      </button>
                    </span>
                    <span className="w-16 text-right tabular-nums text-gray-600">
                      {eur(Number(it.quantity) * Number(it.unit_price || 0))}
                    </span>
                  </li>
                ))}
              </ul>
              </>
            )}

            <div className="mt-4 space-y-1 border-t border-[#F5F5F5] pt-3 text-sm">
              <div className="flex items-center justify-between text-[#737373]">
                <span>Subtotal ({cart.items.length} producto{cart.items.length === 1 ? "" : "s"})</span>
                <span className="tabular-nums">{eur(total)}</span>
              </div>
              <div className="flex items-center justify-between text-[#737373]">
                <span>Saldo disponible</span>
                <span className={`tabular-nums ${saldo < 0 ? "font-semibold text-[#DC2626]" : ""}`}>{eur(saldo)}</span>
              </div>
              <div className="flex items-center justify-between pt-1 text-base font-bold text-[#0A0A0A]">
                <span>Total</span>
                <span className="tabular-nums">{eur(total)}</span>
              </div>
            </div>
            {total > saldo && (
              <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
                Se apunta a su cuenta: quedará en <b>{eur(saldo - total)}</b> (se salda al recargar).
              </div>
            )}
            {mesas.length > 0 && (
              <label className="mt-3 block text-xs text-[#737373]">
                <span className="mb-1 block font-medium">Entrega</span>
                <select
                  value={mesaId}
                  onChange={(e) => setMesaId(e.target.value)}
                  className="h-9 w-full rounded-md border border-[#E5E5E5] bg-white px-2 text-sm text-[#0A0A0A]"
                >
                  <option value="">🛎️ Recoge en barra</option>
                  {mesas.map((m) => (
                    <option key={m.id} value={m.id}>
                      🍽️ {m.label}
                      {m.zone ? ` · ${m.zone}` : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button
              type="button"
              disabled={cartBusy || cart.items.length === 0}
              onClick={enviarABarra}
              className="mt-3 w-full rounded-lg bg-[#0A0A0A] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#262626] disabled:cursor-not-allowed disabled:bg-[#E5E5E5] disabled:text-[#A3A3A3]"
            >
              {cartBusy ? "Enviando…" : "🍹 Enviar a Barra"}
            </button>
            {cart.items.length > 0 && (
              <button
                type="button"
                disabled={cartBusy}
                onClick={() => saveCart([])}
                className="mt-2 w-full text-center text-xs font-medium text-[#DC2626] hover:underline"
              >
                Vaciar carrito
              </button>
            )}
          </div>
        </div>
        </>
      )}

      {/* Modal recarga de saldo (reutiliza el AddCashModal del POS) */}
      {saldoUserId && (
        <AddCashModal
          active={!!saldoUserId}
          handleAddCashModal={() => setSaldoUserId(null)}
          venueID={venueID}
          token={token}
          user_id={saldoUserId}
          setErrorMessage={() => {}}
          onCashAdded={(uid, newBalance) => {
            updateUserCredit?.(uid, newBalance);
            if (String(atendiendo?.id) === String(uid)) {
              setAtendiendo((prev) => (prev ? { ...prev, credit_balance: newBalance } : prev));
            }
            setSaldoUserId(null);
          }}
          currentBalance={
            usersInVenue.find((u) => String(u.id) === String(saldoUserId))?.credit_balance ??
            usersInVenue.find((u) => String(u.id) === String(saldoUserId))?.profile?.credit_balance ??
            0
          }
          memberName={(() => {
            const m = usersInVenue.find((u) => String(u.id) === String(saldoUserId));
            const nom = `${m?.profile?.first_name || ""} ${m?.profile?.last_name || ""}`.trim();
            return nom || undefined;
          })()}
        />
      )}

      {/* Modal vinculación de móvil (QR de un solo uso, TTL corto) */}
      {vinculo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setVinculo(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-[#0A0A0A]">Vincular móvil</h2>
            <p className="mt-1 text-sm text-[#737373]">
              {`${vinculo.socio?.profile?.first_name || ""} ${vinculo.socio?.profile?.last_name || ""}`.trim() || "Socio"}
              : escanea este QR con tu móvil.
            </p>
            <div className="mx-auto mt-4 w-fit rounded-xl border border-[#E5E5E5] bg-white p-3">
              <QRCode value={vinculo.url} size={200} />
            </div>
            <p className="mt-3 text-xs text-[#A3A3A3]">Caduca en {vinculo.min || 5} minutos. Un solo uso en persona.</p>
            <button
              type="button"
              onClick={() => setVinculo(null)}
              className="mt-4 w-full rounded-lg bg-[#0A0A0A] py-2 text-sm font-semibold text-white"
            >
              Hecho
            </button>
          </div>
        </div>
      )}

      {/* Modal nueva entrada */}
      {showNueva && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-24" onClick={() => setShowNueva(false)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Nueva entrada</h2>
              <button type="button" onClick={() => setShowNueva(false)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <input
              autoFocus
              value={nuevaQ}
              onChange={(e) => setNuevaQ(e.target.value)}
              placeholder="Nombre, nº de socio o email…"
              className="mb-3 h-11 w-full rounded-lg border border-gray-300 px-3 text-sm"
            />
            {nuevaQ.trim().length < 2 && (
              <div className="mb-2 rounded-lg bg-[#F7F7F3] px-3 py-2 text-xs text-[#737373]">
                Escribe al menos <b>2 letras</b> del nombre, nº de socio o email.
              </div>
            )}
            {nuevaBuscando && nuevaQ.trim().length >= 2 && (
              <div className="mb-2 px-1 text-xs text-[#737373]">Buscando…</div>
            )}
            <div className="max-h-72 overflow-y-auto">
              {nuevaResults
                .filter((c) => !usersInVenue.some((u) => String(u.id) === String(c.id)))
                .map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => registrarEntrada(c)}
                    className="flex w-full items-center gap-3 border-b border-gray-100 px-2 py-2 text-left hover:bg-gray-50"
                  >
                    <MemberPhoto
                      path={c?.profile?.image_path || c?.profile?.documents_path}
                      name={c?.profile?.first_name || c?.email || ""}
                      className="h-9 w-9 rounded-lg object-cover"
                    />
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {c?.profile?.first_name} {c?.profile?.last_name}
                      <span className="ml-2 text-xs text-gray-400">{c?.email}</span>
                    </span>
                    <span className="text-xs font-semibold text-violet-600">Entrar →</span>
                  </button>
                ))}
              {nuevaQ.trim().length >= 2 && !nuevaBuscando && nuevaResults.length === 0 && (
                <div className="p-4 text-center text-sm text-[#A3A3A3]">Sin resultados.</div>
              )}
              {nuevaQ.trim().length >= 2 && !nuevaBuscando && nuevaResults.length > 0 &&
                nuevaResults.every((c) => usersInVenue.some((u) => String(u.id) === String(c.id))) && (
                <div className="p-4 text-center text-sm text-[#A3A3A3]">
                  Los socios encontrados <b>ya están en sala</b>.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
